import { get } from "lodash";
import { Item } from "constants/product-collection";
import { AllocationBagType, forEachAllocationBag, getAllocationBagValue } from "../../allocation-dimension";
import { generateDoorMap, getMetricValuesFromNewBagData, getMetricValuesFromOldBagData } from "./formula-helpers";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { ValueGetterParams } from "ag-grid-community";

export const STORE_WITH_UNITS = ({ factoryOptions, getterParams }) => {
    const { data, context, column }: ValueGetterParams = getterParams;
    const { allocationBagRef } = context;
    const { deliveryId, doors, useSize } = factoryOptions;
    const doorsMap = generateDoorMap(doors);

    const doorsWithUnits = new Set();

    /**
     * New allocation bag
     */
    if (allocationBagRef?.allocationBag) {
        const itemId = get(data, Item.FIELDS.ID);
        const allocationBag = allocationBagRef?.allocationBag;

        if (deliveryId && deliveryId !== "") {
            for (const door of doors) {
                let bulkCount = 0;
                if (useSize) {
                    const { size } = data;
                    bulkCount = getAllocationBagValue(AllocationBagType.allocations, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                        size,
                    ]);
                } else {
                    bulkCount = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                    ]);
                }

                if (bulkCount > 0 && !doorsWithUnits.has(door.id)) {
                    doorsWithUnits.add(door.id);
                }
            }
        } else {
            if (useSize) {
                const { size } = data;
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.allocations, (dimensions, value) => {
                    const { doorIdValue, shouldSkip, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        useSize,
                        size
                    );

                    if (shouldSkip) {
                        return;
                    }

                    if (bulkCount > 0 && !doorsWithUnits.has(doorIdValue)) {
                        doorsWithUnits.add(doorIdValue);
                    }
                });
            } else {
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.bulks, (dimensions, value) => {
                    const { doorIdValue, shouldSkip, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        false
                    );

                    if (shouldSkip) {
                        return;
                    }

                    if (bulkCount > 0 && !doorsWithUnits.has(doorIdValue)) {
                        doorsWithUnits.add(doorIdValue);
                    }
                });
            }
        }
    } else {
        /**
         * Old allocation bag
         */
        let cols: string[][] = [];
        const size: string = data.size;
        if (data.allocations && data.allocations.current) {
            cols = data.allocations.current.r.map((r) => r.c);
        } else if (data.bulks && data.bulks.r) {
            cols = data.bulks.r.map((r) => r.c);
        } else if (data.bulks && data.bulks.current && data.bulks.current.r) {
            cols = data.bulks.current.r.map((r) => r.c);
        }

        cols.forEach((col) => {
            const { doorIdValue, bulkCount, shouldSkip } = getMetricValuesFromOldBagData(
                deliveryId,
                doorsMap,
                col,
                useSize,
                size
            );

            if (shouldSkip) {
                return;
            }

            if (bulkCount > 0 && !doorsWithUnits.has(doorIdValue)) {
                doorsWithUnits.add(doorIdValue);
            }
        });
    }

    /**
     * HACK. When getting row group agg value, it's very expensive to loop through the entire list of doors per children row (588 doors, 5 children rows = ~300ms on M1 machine).
     * Instead, we store `doorsWithUnits` map inside row node and re-use it when getting agg value.
     */
    const colTypesForDoorsWithUnits = [
        ColumnTypes.AllDimensionTotal,
        ColumnTypes.AllocationDimensionTotal,
        ColumnTypes.ClassificationTotal,
    ];
    if (colTypesForDoorsWithUnits.includes(column.getColDef().type as ColumnTypes)) {
        getterParams.data._doorsWithUnits = {
            ...getterParams.data._doorsWithUnits,
            [column.getColId()]: doorsWithUnits,
        };
    }

    if (doorsWithUnits.size === 0) {
        return undefined;
    }

    return doorsWithUnits.size;
};
